import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, ChevronRight, Phone } from "lucide-react";
import { PRODUCTS, IMAGES } from "@/data/mockData";
import { SITE_CONFIG } from "@/data/siteConfig";

export default function ProductDetail() {
  const { id } = useParams();
  const product = PRODUCTS.find((item) => item.id === id) || PRODUCTS[0];
  const [activeImage, setActiveImage] = useState(0);
  const related = PRODUCTS.filter((item) => item.id !== product.id).slice(0, 3);

  return (
    <div data-testid="product-page" className="bg-cream">
      <div className="container-wide pt-6">
        <nav
          data-testid="breadcrumbs"
          className="flex items-center gap-2 text-xs text-ink"
          aria-label="Breadcrumb"
        >
          <Link to="/" className="hover:text-espresso">Home</Link>
          <ChevronRight className="w-3 h-3" strokeWidth={1.7} />
          <Link to="/shop" className="hover:text-espresso">Shop</Link>
          <ChevronRight className="w-3 h-3" strokeWidth={1.7} />
          <span className="text-espresso truncate">{product.name}</span>
        </nav>
      </div>

      <section className="container-wide pt-8 md:pt-12 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7" data-testid="product-gallery">
            <div className="aspect-[4/5] overflow-hidden bg-oat mb-4">
              <img
                src={product.gallery[activeImage]}
                alt={`${product.name}, view ${activeImage + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="grid grid-cols-4 gap-3">
              {product.gallery.map((src, index) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setActiveImage(index)}
                  data-testid={`gallery-thumb-${index}`}
                  className={`aspect-square overflow-hidden bg-oat border ${
                    activeImage === index ? "border-forest" : "border-transparent"
                  }`}
                  aria-label={`Show product image ${index + 1}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <p className="text-sm font-semibold text-forest mb-4">{product.tag}</p>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1]">
                {product.name}
              </h1>
              <p className="mt-4 text-ink text-base md:text-lg leading-relaxed">
                {product.descriptor}
              </p>
              <p className="mt-5 text-sm text-ink">
                Product details and colors shown here are illustrative. View the
                current selection and pricing on Shopify.
              </p>

              <div className="mt-8 p-5 bg-cactus-wash border border-forest/20">
                <p className="text-sm leading-relaxed text-ink">
                  {SITE_CONFIG.inventory.notice}
                </p>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a
                  href={SITE_CONFIG.links.shopify}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="product-shopify-cta"
                  className="btn-primary"
                >
                  Continue to Shopify
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.7} />
                </a>
                <a href={SITE_CONFIG.phone.href} className="btn-secondary">
                  <Phone className="w-4 h-4" strokeWidth={1.7} />
                  Call the Shop
                </a>
              </div>

              <p className="mt-9 pt-8 border-t border-rule text-base leading-relaxed text-espresso font-serif italic">
                {product.warm}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-bone border-y border-rule py-20 md:py-24">
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-6">
            <h2 className="font-sans text-2xl md:text-3xl font-semibold mb-7">
              Illustrative specifications
            </h2>
            <dl className="border-t border-rule" data-testid="product-specs">
              {[
                ["Fiber", product.fiber],
                ["Weight", product.weight],
                ["Yardage", product.yardage],
                ["Gauge", product.gauge],
                ["Care", product.care],
                ["Color", product.color],
              ].map(([label, value]) => (
                <div key={label} className="grid grid-cols-3 gap-4 py-4 border-b border-rule">
                  <dt className="text-xs text-ink">{label}</dt>
                  <dd className="col-span-2 text-sm text-espresso">{value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="lg:col-span-6">
            <h2 className="font-sans text-2xl md:text-3xl font-semibold mb-7">
              A preview of the details
            </h2>
            <ul className="space-y-5">
              {product.love.map((row) => (
                <li key={row.label} className="flex items-start gap-4">
                  <Check className="w-4 h-4 text-forest mt-1 shrink-0" strokeWidth={2} />
                  <div>
                    <p className="text-xs text-ink">{row.label}</p>
                    <p className="text-espresso font-medium">{row.value}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="container-wide">
          <div className="flex items-end justify-between gap-6 mb-9">
            <h2 className="font-sans text-2xl md:text-3xl font-semibold">More catalog previews</h2>
            <Link to="/shop" className="btn-tertiary hidden sm:inline-flex">
              Back to Shop
              <ArrowRight className="w-4 h-4" strokeWidth={1.7} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-7">
            {related.map((item) => (
              <Link key={item.id} to={`/shop/${item.id}`} className="group block">
                <div className="aspect-[4/3] overflow-hidden bg-oat">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="font-sans font-semibold mt-4">{item.name}</h3>
                <p className="text-sm text-ink mt-1">{item.weight} · {item.color}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cactus-wash border-t border-rule py-16">
        <div className="container-editorial grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div className="aspect-[16/10] overflow-hidden bg-oat">
            <img src={IMAGES.store} alt="Needle-arts supplies in the shop" className="w-full h-full object-cover" />
          </div>
          <div>
            <h2 className="font-sans text-2xl md:text-3xl font-semibold">See the shop in Las Vegas</h2>
            <p className="mt-3 text-ink leading-relaxed">
              Visit Booth {SITE_CONFIG.location.booth} inside the Fantastic Indoor Swap Meet.
            </p>
            <Link to="/visit" className="btn-tertiary mt-6">
              Plan Your Visit
              <ArrowRight className="w-4 h-4" strokeWidth={1.7} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
